/**
 * @fileoverview Stops a stray file drop from navigating the SPA away.
 *
 * Dropping a file anywhere the page does not explicitly handle makes the
 * browser navigate to that file. In a single-page app that discards everything
 * held in memory: the unsent draft, the open session, in-flight state. Missing
 * the composer by a few pixels should not cost the user their message.
 *
 * This suppresses only the browser's DEFAULT behavior. It never consumes the
 * event, so real drop targets — the composer, and any `<input type="file">` —
 * keep working; their own handlers run first, on the way down and along the
 * bubble path, before this one sees the event on `document`.
 *
 * @module utils/fileDropGuard
 */

/**
 * Whether a drag carries files rather than text.
 *
 * Text dragged within or between inputs must keep its native behavior, so
 * anything that is not a file drag is left completely alone.
 *
 * @param {DragEvent} event - Drag or drop event
 * @returns {boolean} True when the drag carries files
 */
function isFileDrag(event) {
  return Array.from(event.dataTransfer?.types || []).includes('Files');
}

/**
 * Whether the event landed on an element that legitimately accepts file drops.
 *
 * A file input accepts drops natively, with no JS handler of its own — so
 * suppressing the default there would silently break it.
 *
 * @param {DragEvent} event - Drag or drop event
 * @returns {boolean} True when the target handles file drops itself
 */
function isNativeDropTarget(event) {
  const target = event.target;
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(target.closest('input[type="file"]'));
}

/**
 * Install the guard.
 *
 * Both `dragover` and `drop` need suppressing: without preventDefault on
 * `dragover` the browser never treats the page as a drop target, and without it
 * on `drop` it navigates.
 *
 * @param {Document} [doc=document] - Document to guard (injectable for tests)
 * @returns {() => void} Removes the guard
 */
export function installFileDropGuard(doc = document) {
  const suppress = (event) => {
    if (!isFileDrag(event)) return;
    if (isNativeDropTarget(event)) return;
    event.preventDefault();
  };

  doc.addEventListener('dragover', suppress);
  doc.addEventListener('drop', suppress);

  return () => {
    doc.removeEventListener('dragover', suppress);
    doc.removeEventListener('drop', suppress);
  };
}
